import React, { ReactNode, useEffect, useMemo } from 'react';
import { ChakraProvider } from '@chakra-ui/react';
import { CompanyBrand } from '../models/companyBrand';
import buildBrandTheme from '../theme/brandTheme';
import { useSkin } from '../theme/SkinProvider';
import { loadBrandFont } from '../theme/lazyFonts'; // item 1516
import { usePortalBrand } from '../hooks/useCustomerPortal';

// item 852 — the RNWY Portal wears its OWN brand (Settings → Proposal Portal),
// not the tenant company brand: the portal is RNWY's product surface, so it boots into
// the RNWY kit (purple #6332FB, Albert Sans) and stays admin-tunable. Replaces
// BrandThemeProvider in the Customer Builder branch of App.tsx. The skin is FORCED to
// 'basix' (aurora) — the Accountant ledger is an internal look; the customer never sees it.
export const RNWY_PORTAL_FALLBACK: CompanyBrand = {
  companyName: 'RNWY Portal',
  logoDataUrl: null,
  primaryColor: '#6332FB',
  accentColor: '#6332FB',
  successColor: null,
  warningColor: null,
  errorColor: null,
  fontFamily: 'Albert Sans',
  slogan: null,
  onboardingCompleted: true,
};

const PortalThemeProvider = ({ children }: { children: ReactNode }) => {
  const { data } = usePortalBrand();
  const { ledgerTone, colorPref } = useSkin();

  const brand = useMemo<CompanyBrand>(() => (data ? {
    ...RNWY_PORTAL_FALLBACK,
    companyName: data.portalName || RNWY_PORTAL_FALLBACK.companyName,
    logoDataUrl: data.logoDataUrl,
    primaryColor: data.primaryColor || RNWY_PORTAL_FALLBACK.primaryColor,
    accentColor: data.accentColor || RNWY_PORTAL_FALLBACK.accentColor,
    fontFamily: data.fontFamily || RNWY_PORTAL_FALLBACK.fontFamily,
  } : RNWY_PORTAL_FALLBACK), [data]);

  // item 1516 — the portal's face may be a lazy-loaded family (Settings → Proposal Portal picks
  // from the same curated list); pull its chunk the moment it resolves.
  const { fontFamily } = brand;
  useEffect(() => { loadBrandFont(fontFamily); }, [fontFamily]);

  const theme = useMemo(
    () => buildBrandTheme(brand, 'basix', ledgerTone, colorPref),
    [brand, ledgerTone, colorPref],
  );

  return <ChakraProvider theme={theme}>{children}</ChakraProvider>;
};

export default PortalThemeProvider;
